import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { useMultipleFileUpload, useNotification } from "@hooks/index";
import { isEqual } from "lodash";
import moment from "moment-timezone";
import { useForm, Controller } from "react-hook-form";

import "@assets/css/pages/employer/listeEmployer.css";

import ModalIndex from "@Modals/layout/index";
import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import DateRangePickerDropdown from "@components/dateRange/DropdownDateRange";
import InputFileMultipleArh from "@components/input/InputFileMultiple";
import { Input, TextArea } from "@components/tailwindComponents";

import { REGEX_VALID_FILES } from "@helpers/_constants";
import {
  convertToFormData,
  convertURLToFile,
  // validateDateIntoAPeriod,
} from "@helpers/_functions";
import { reactMemo } from "@helpers/retyped-functions";
import { DataFile, EventUnavalaibilityService } from "@services/all.services";

function NewEvent({
  onClickClose,
  show,
  employeeId,
  unaivalabilitySelected,
  oldFilesFromBD,
  event,
}) {
  const { t } = useTranslation("pages.employés.indisponibility");
  const { showError, showInfo } = useNotification();
  const [isSaved, setIsSaved] = useState(false);
  const {
    reset,
    watch,
    control,
    handleSubmit,
    setValue,
    clearErrors,
    //getValues
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const {
    oldFiles,
    removeOldFiles,
    toDeleteOldFiles,
    status,
    handleUploadStatusChange,
    /*handleRemoveNewFiles,*/
    handleDeleteOldFiles,
    resetInputFiles,
  } = useMultipleFileUpload({ defaultFiles: oldFilesFromBD });
  const period = watch("period");

  useEffect(() => {
    if (period?.startDate && period?.endDate) clearErrors("period");
  }, [period]);

  useEffect(() => {
    if (!show) {
      reset({
        period: {
          startDate: null,
          endDate: null,
        },
        alertDate: null,
        title: "",
        notes: "",
      });
    } else if (show && event?._id) {
      reset({
        period: {
          startDate: moment(event?.beginPeriod)?.isValid()
            ? moment(event?.beginPeriod, "YYYY-MM-DD").toDate()
            : null,
          endDate: moment(event?.endPeriod)?.isValid()
            ? moment(event?.endPeriod, "YYYY-MM-DD").toDate()
            : null,
        },
        alertDate: moment(event?.alertDate).isValid()
          ? moment(event?.alertDate, "YYYY-MM-DD").toDate()
          : null,
        title: event?.title,
        notes: event?.notes,
      });
    }
    clearErrors(["period", "title", "alertDate"]);
    resetInputFiles();
    setIsSaved(false);
  }, [show]);

  function handleFormError(errors) {
    const errorArray = Object.values(errors);
    if (errorArray.some((err) => err.type === "required")) {
      showError("field_obligatory");
      return;
    }
  }

  const onCreateEvent = async (formValues) => {
    try {
      setIsSaved(true);
      if (!unaivalabilitySelected?._id || !employeeId)
        return showError("error");

      if (!isEqual(status, "done") && formValues?.files?.length) {
        return showInfo("please_wait_uploading");
      }

      const dataFiles = [];
      if (event?._id && oldFiles?.length) {
        for (let file of oldFiles) {
          if (file?.location) {
            dataFiles.push(await convertURLToFile(file?.location, file));
            await DataFile.deleteFile(file?._id);
          }
        }
      }
      const requestData = {
        title: formValues?.title,
        beginPeriod: moment(formValues?.period?.startDate).format("YYYY-MM-DD"),
        endPeriod: moment(formValues?.period?.endDate).format("YYYY-MM-DD"),
        alertDate: moment(formValues?.alertDate).isValid()
          ? moment(formValues?.alertDate).format("YYYY-MM-DD")
          : "",
        notes: formValues?.notes,
        entreprise: localStorage.getItem("enterpriseId"),
        Employer: employeeId,
        files: dataFiles.concat(formValues.files),
        unavailability: unaivalabilitySelected?._id,
      };

      if (toDeleteOldFiles?.length) {
        await removeOldFiles();
      }
      let response = {};
      if (event?._id) {
        response = await EventUnavalaibilityService.update(
          event?._id,
          convertToFormData(requestData),
        );
      } else {
        response = await EventUnavalaibilityService.insert(
          convertToFormData(requestData),
        );
      }
      if (!response || response?.data?.msg === "superposition") {
        showError("superposition");
        setIsSaved(false);
        return;
      }
      onClickClose(null);
      setIsSaved(false);
    } catch (err) {
      showError("error");
      console.log("Error:" + err);
      setIsSaved(false);
    }
  };

  const resetDateFilter = () => {
    setValue("period", {
      startDate: null,
      endDate: null,
    });
  };

  return (
    <ModalIndex
      isOpen={show}
      onClickClose={onClickClose}
      title={
        event?._id ? t("Modifier un événement") : t("Ajouter un événement")
      }
      buttonOptions={{
        click: handleSubmit(onCreateEvent, handleFormError),
        color: "green",
        title: t("Enregistrer"),
      }}
      buttonCancelOptions={{
        click: () => onClickClose(),
        color: "outlined",
        title: t("Annuler"),
      }}
      isLoading={isSaved}
    >
      <div className="arh-w-full arh-space-y-2.5">
        <div>
          <Controller
            control={control}
            name="title"
            rules={{ required: true }}
            render={({
              field: { onChange, value, name },
              fieldState: { invalid },
            }) => (
              <Input
                invalid={invalid}
                label={t("Titre de l’événement")}
                name={name}
                value={value}
                placeholder={t("Titre de l’événement")}
                onChange={onChange}
              />
            )}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="period"
            rules={{
              required: true,
              validate: (value) => {
                // if (
                //   unaivalabilitySelected?.beginningOfPeriod &&
                //   value?.startDate &&
                //   unaivalabilitySelected?.endOfPeriod &&
                //   value?.endDate
                // ) {
                //   if (
                //     !validateDateIntoAPeriod(
                //       value?.startDate,
                //       unaivalabilitySelected?.beginningOfPeriod,
                //       unaivalabilitySelected?.endOfPeriod,
                //     ) ||
                //     !validateDateIntoAPeriod(
                //       value?.endDate,
                //       unaivalabilitySelected?.beginningOfPeriod,
                //       unaivalabilitySelected?.endOfPeriod,
                //     )
                //   ) {
                //     showInfo("out_of_period_unav");
                //     return false;
                //   }
                // } else 
                if (!(value?.startDate && value?.endDate)) {
                  return false;
                }

                return true;
              },
            }}
            render={({ field: { value, name }, fieldState: { invalid } }) => (
              <div
                className={`datepicker_modals_parcours arh-mb-[1.75rem] arh-w-full`}
              >
                <label
                  className={`arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium  ${
                    invalid ? "arh-text-[#F60A20]" : "arh-text-darkColor"
                  } `}
                >
                  {t("Période")}
                </label>
                <div className="dateRange_green DateRangePicker_customisation DateRangePicker_customisation--green arh-relative">
                  <DateRangePickerDropdown
                    key="date_range_periode"
                    classParent="arhDatePicker"
                    idInput="daterange"
                    className="input_simple !arh-pl-[1.5rem]"
                    dateSelected={value}
                    name={name}
                    textInput={t("Période")}
                    SaveCloseRange={(date) => setValue("period", date?.[0])}
                    showResetFilter={
                      period.startDate && period.endDate ? true : false
                    }
                    onResetFilter={resetDateFilter}
                    classSwitch="c-switch-pill-checked--green--color--to--left"
                    classBackground="green"
                    classIconeCalendarPositionInput=" !arh-right-[0.5rem] "
                    invalid={invalid}
                  />
                </div>
              </div>
            )}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="alertDate"
            // rules={{
            //   validate: (value) => {
            //     if (period?.startDate && value && period?.endDate) {
            //       if (
            //         !validateDateIntoAPeriod(
            //           value,
            //           period?.startDate,
            //           period?.endDate,
            //         )
            //       ) {
            //         showInfo("out_of_period_event");
            //         return false;
            //       }
            //     }
            //     return true;
            //   },
            // }}
            render={({
              field: { onChange, value, name },
              fieldState: { invalid },
            }) => (
              <div
                className={`arh-calendar-reimbursment flex-column calendar_with_btn employerThemeDatePicker arh-mb-[1.75rem] arh-flex arh-w-full 
              arh-text-darkColor
                `}
              >
                <label
                  className={`arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium  ${
                    invalid ? "arh-text-[#F60A20]" : "arh-text-darkColor"
                  } `}
                >
                  {t("Définir une alerte")}
                </label>

                <DefaultDatePicker
                  className="focus_green dateOfBirth arh-relative"
                  name={name}
                  placeholder={t("Définir une alerte")}
                  dateValue={value}
                  setDateValue={onChange}
                  classPosition="calendar_left  arh-w-full"
                  invalid={invalid}
                  inputDatePicker=" !arh-pl-[1.5rem] "
                />
              </div>
            )}
          />
        </div>
        <div>
          <div className="arh-my-[1rem] arh-w-full arh-text-center arh-font-poppins arh-text-[16px] arh-font-semibold arh-text-darkColor">
            {t("Upload fichier")}
          </div>
          <div className="input_file_theme_employer arh-w-full">
            <Controller
              control={control}
              name="files"
              defaultValue={[]}
              render={({ field: { onChange, value } }) => (
                <InputFileMultipleArh
                  handleChangeStatus={handleUploadStatusChange(value, onChange)}
                  // handleRemoveNewFiles={(file) => handleRemoveNewFiles(file, onChange)}
                  classUploadFile="employer_theme_upload GreenThemeSelect !arh-mb-0"
                  regexp={REGEX_VALID_FILES}
                  hideTitle={true}
                  allFile={true}
                  dataFile={oldFiles}
                  onDeleteOldFile={handleDeleteOldFiles}
                />
              )}
            />
          </div>
        </div>

        <div>
          <Controller
            control={control}
            name="notes"
            defaultValue={event?.notes || ""}
            render={({ field: { onChange, value, name } }) => (
              <TextArea
                label={t("Notes")}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={t("Notes")}
              />
            )}
          />
        </div>
      </div>
    </ModalIndex>
  );
}
export default reactMemo(NewEvent);
